import { useState } from "react";
import { Download, FileText } from "lucide-react";

import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { MyLeaveBalancesTable } from "../components/MyLeaveBalancesTable";
import { useAsyncData } from "../hooks";
import { exportToExcel, exportToPdf } from "../lib/exportUtils";
import type { LeaveBalance } from "../types";

export default function MyLeaveBalancesPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const balances = useAsyncData(() => leaveApi.myBalances(year), [year]);
  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const allTypes = useAsyncData(() => leaveApi.activeLeaveTypes(), []);

  const loading = balances.loading || employee.loading || allTypes.loading;
  const error = balances.error || employee.error;

  async function handleExportPdf() {
    if (!balances.data) return;
    setExporting(true);
    try {
      const { headers, rows } = buildExportData(balances.data);
      await exportToPdf({
        title: `My Leave Balances — ${year}`,
        subtitle: `${employee.data?.firstName ?? ""} ${employee.data?.lastName ?? ""} · Generated: ${new Date().toLocaleString()}`,
        headers,
        rows,
        filename: `my-leave-balances-${year}`,
      });
    } finally {
      setExporting(false);
    }
  }

  async function handleExportExcel() {
    if (!balances.data) return;
    setExportingExcel(true);
    try {
      const { headers, rows } = buildExportData(balances.data);
      await exportToExcel(`My Leave Balances — ${year}`, headers, rows, `my-leave-balances-${year}`);
    } finally {
      setExportingExcel(false);
    }
  }

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="My Leave Balances"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <input
              type="number"
              className="w-28 rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={year}
              min={2020}
              max={2100}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting || !balances.data}
              className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
              title="Export as PDF"
            >
              <FileText className="h-4 w-4 text-red-500" />
              PDF
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel || !balances.data}
              className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
              title="Export as Excel"
            >
              <Download className="h-4 w-4 text-emerald-600" />
              Excel
            </button>
          </div>
        }
      />

      {loading ? <LeaveLoading label="Loading your balances…" /> : null}
      {error ? <LeaveError message={error} onRetry={balances.reload} /> : null}

      {balances.data ? (
        <MyLeaveBalancesTable
          items={balances.data}
          employeeGender={employee.data?.gender ?? null}
          leaveTypesMap={
            allTypes.data
              ? Object.fromEntries(allTypes.data.map((t) => [t.id, t]))
              : {}
          }
        />
      ) : null}
    </div>
  );
}

function buildExportData(balances: LeaveBalance[]) {
  const headers = [
    "Leave Type",
    "Year",
    "Entitled",
    "Brought Forward",
    "Pending",
    "Used",
    "Forfeited",
    "Remaining",
  ];
  const rows = balances.map((b) => [
    b.leaveTypeName,
    b.leaveYear,
    b.entitledDays,
    b.broughtForwardDays,
    b.pendingDays,
    b.usedDays,
    b.forfeitedDays,
    b.remainingDays,
  ]);
  return { headers, rows };
}
